// Push Notification Service for Tour Finder
// Handles service worker registration and push subscription

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY;

class PushNotificationService {
  constructor() {
    this.swRegistration = null;
    this.subscription = null;
    this.isSupported = 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Check if push notifications are supported
  checkSupport() {
    if (!('serviceWorker' in navigator)) {
      console.log('Service Worker not supported');
      return { supported: false, reason: 'Service Worker not supported in this browser' };
    }
    if (!('PushManager' in window)) {
      console.log('Push notifications not supported');
      return { supported: false, reason: 'Push notifications not supported in this browser' };
    }
    if (!('Notification' in window)) {
      console.log('Notifications not supported');
      return { supported: false, reason: 'Notifications not supported in this browser' };
    }
    return { supported: true, reason: null };
  }

  // Get current permission state
  getPermissionState() {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission; // 'granted', 'denied', or 'default'
  }

  // Register the service worker
  async registerServiceWorker() {
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }

    try {
      this.swRegistration = await navigator.serviceWorker.register('/sw.js', {
        scope: '/'
      });
      console.log('Service Worker registered:', this.swRegistration);
      
      // Wait for the service worker to be ready
      await navigator.serviceWorker.ready;
      console.log('Service Worker is ready');
      
      return this.swRegistration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  // Request notification permission
  async requestPermission() {
    if (!('Notification' in window)) {
      throw new Error('Notifications not supported');
    }

    const permission = await Notification.requestPermission();
    console.log('Notification permission:', permission);
    return permission;
  }

  // Subscribe to push notifications
  async subscribeToPush(apiUrl, authToken) {
    if (!this.swRegistration) {
      await this.registerServiceWorker();
    }

    // Check permission
    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      throw new Error('Notification permission denied');
    }

    // Get VAPID public key from backend
    let vapidPublicKey = VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
      try {
        const response = await fetch(`${apiUrl}/api/push/vapid-public-key`);
        const data = await response.json();
        vapidPublicKey = data.publicKey;
      } catch (error) {
        console.error('Failed to get VAPID public key:', error);
        throw new Error('Failed to get push notification configuration');
      }
    }

    // Convert VAPID key to Uint8Array
    const applicationServerKey = this.urlBase64ToUint8Array(vapidPublicKey);

    try {
      // Check for existing subscription
      let subscription = await this.swRegistration.pushManager.getSubscription();
      
      if (subscription) {
        console.log('Existing push subscription found');
        this.subscription = subscription;
      } else {
        // Create new subscription
        subscription = await this.swRegistration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: applicationServerKey
        });
        console.log('New push subscription created:', subscription);
        this.subscription = subscription;
      }

      // Send subscription to backend
      await this.saveSubscriptionToServer(apiUrl, authToken, subscription);
      
      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      throw error;
    }
  }

  // Save subscription to server
  async saveSubscriptionToServer(apiUrl, authToken, subscription) {
    try {
      const response = await fetch(`${apiUrl}/api/push/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          subscription: subscription.toJSON(),
          device_info: {
            userAgent: navigator.userAgent,
            platform: navigator.platform,
            language: navigator.language,
            screenWidth: window.screen.width,
            screenHeight: window.screen.height
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to save subscription to server');
      }

      const data = await response.json();
      console.log('Subscription saved to server:', data);
      return data;
    } catch (error) {
      console.error('Failed to save subscription:', error);
      throw error;
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe(apiUrl, authToken) {
    if (!this.swRegistration) {
      return;
    }

    try {
      const subscription = await this.swRegistration.pushManager.getSubscription();
      if (subscription) {
        // Unsubscribe locally
        await subscription.unsubscribe();
        
        // Remove from server
        await fetch(`${apiUrl}/api/push/unsubscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint
          })
        });
        
        console.log('Unsubscribed from push notifications');
        this.subscription = null;
      }
    } catch (error) {
      console.error('Unsubscribe failed:', error);
      throw error;
    }
  }

  // Check if currently subscribed
  async isSubscribed() {
    if (!this.swRegistration) {
      try {
        this.swRegistration = await navigator.serviceWorker.ready;
      } catch {
        return false;
      }
    }

    const subscription = await this.swRegistration.pushManager.getSubscription();
    return !!subscription;
  }

  // Get current subscription
  async getSubscription() {
    if (!this.swRegistration) {
      try {
        this.swRegistration = await navigator.serviceWorker.ready;
      } catch {
        return null;
      }
    }

    return await this.swRegistration.pushManager.getSubscription();
  }

  // Helper: Convert VAPID key from base64 to Uint8Array
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Show a local notification (for testing)
  async showLocalNotification(title, options = {}) {
    if (!this.swRegistration) {
      await this.registerServiceWorker();
    }

    const defaultOptions = {
      body: 'This is a test notification',
      icon: '/icons/icon-192x192.png',
      badge: '/icons/icon-72x72.png',
      vibrate: [200, 100, 200],
      tag: 'test-notification',
      renotify: true
    };

    return this.swRegistration.showNotification(title, {
      ...defaultOptions,
      ...options
    });
  }

  // Clear app badge on Home Screen
  async clearBadge() {
    if ('clearAppBadge' in navigator) {
      try {
        await navigator.clearAppBadge();
        console.log('App badge cleared');
      } catch (error) {
        console.error('Failed to clear app badge:', error);
      }
    }
    
    // Also notify service worker
    if (this.swRegistration) {
      this.swRegistration.active?.postMessage({ type: 'CLEAR_BADGE' });
    }
  }

  // Set app badge count
  async setBadge(count) {
    if ('setAppBadge' in navigator) {
      try {
        if (count > 0) {
          await navigator.setAppBadge(count);
          console.log('App badge set to:', count);
        } else {
          await navigator.clearAppBadge();
          console.log('App badge cleared');
        }
      } catch (error) {
        console.error('Failed to set app badge:', error);
      }
    }
    
    // Also notify service worker
    if (this.swRegistration) {
      this.swRegistration.active?.postMessage({ type: 'SET_BADGE', count });
    }
  }

  // Check if app is running in standalone mode (added to Home Screen)
  isStandalone() {
    return window.matchMedia('(display-mode: standalone)').matches || 
           window.navigator.standalone === true;
  }

  // Check if device is online
  isOnline() {
    return navigator.onLine;
  }
}

// Export singleton instance
export const pushNotificationService = new PushNotificationService();
export default pushNotificationService;
